// Iter 84 · Phase 3 — Operations Command Center
// Portfolio-wide view for Operations Managers. Aggregates per-project
// punch counters + recent activity + red-flag detection. Reuses
// existing per-project endpoints; ZERO new backend.
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, AlertTriangle, CheckCircle2, Clock, Package,
  Share2, Inbox as InboxIcon, Activity, Filter,
} from "lucide-react";
import { api } from "@/lib/api";

const RED_FLAG_THRESHOLD = 3;  // 3+ overdue = red flag

export default function OperationsCommandCenter() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filterPm, setFilterPm] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/projects");
        const projects = data.projects || data || [];
        const out = await Promise.all(projects.slice(0, 50).map(async (p) => {
          // Reuse the existing Iter 82.2 summary endpoint.
          const summary = await api.get(`/projects/${p.id}/punch-items/summary`)
            .then((r) => r.data || {})
            .catch(() => ({}));
          const punches = await api.get(`/projects/${p.id}/punch-items`,
                                          { params: { limit: 50 } })
            .then((r) => r.data.items || r.data.punch_items || [])
            .catch(() => []);
          const overdue = punches.filter((x) =>
            ["punch.overdue", "overdue", "escalated"].includes(x.status) ||
            (x.due_date && new Date(x.due_date) < new Date()
             && !["closed", "rejected"].includes(x.status))).length;
          const open = punches.filter((x) => x.status === "open").length;
          const ready = punches.filter((x) => x.status === "ready_for_review").length;
          const closed = punches.filter((x) => x.status === "closed").length;
          const total = punches.length || 1;
          const closeoutPct = Math.round((closed / total) * 100);
          return {
            id: p.id,
            name: p.name,
            project_number: p.project_number,
            location: p.location,
            pm_id: p.pm_id || p.project_manager_id,
            pm_name: p.pm_name || "—",
            status: p.status,
            open,
            overdue,
            ready,
            closed,
            closeoutPct,
            redFlag: overdue >= RED_FLAG_THRESHOLD,
            summary,
          };
        }));
        setRows(out);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterPm !== "all" && r.pm_id !== filterPm) return false;
      return true;
    });
  }, [rows, filterPm, filterStatus]);

  const totals = useMemo(() => ({
    projects: filtered.length,
    open: filtered.reduce((s, r) => s + r.open, 0),
    overdue: filtered.reduce((s, r) => s + r.overdue, 0),
    ready: filtered.reduce((s, r) => s + r.ready, 0),
    closed: filtered.reduce((s, r) => s + r.closed, 0),
    redFlag: filtered.filter((r) => r.redFlag).length,
  }), [filtered]);

  const topProblem = useMemo(() =>
    [...filtered].sort((a, b) => b.overdue - a.overdue).slice(0, 5),
    [filtered]);

  const pms = useMemo(() => {
    const m = new Map();
    for (const r of rows) if (r.pm_id) m.set(r.pm_id, r.pm_name);
    return Array.from(m.entries());
  }, [rows]);

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="ops-command-center">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              ForgedOps · Operations Command Center
            </div>
            <h1 className="text-2xl font-semibold mt-1">Portfolio Pulse</h1>
            <div className="text-[12px] text-[var(--fop-text-3)] mt-1">
              {totals.projects} project{totals.projects === 1 ? "" : "s"} · Updated just now
            </div>
          </div>
        </header>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Stat label="Projects" value={totals.projects} icon={Building2} />
          <Stat label="Open Punches" value={totals.open} icon={Clock} />
          <Stat label="Overdue" value={totals.overdue} icon={AlertTriangle}
                  tone={totals.overdue > 0 ? "warn" : "ok"} />
          <Stat label="Ready Review" value={totals.ready} icon={Activity} />
          <Stat label="Closed" value={totals.closed} icon={CheckCircle2} tone="ok" />
          <Stat label="Red Flags" value={totals.redFlag} icon={AlertTriangle}
                  tone={totals.redFlag > 0 ? "red" : "ok"} />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <Filter size={12} className="text-[var(--fop-text-3)]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-[11px] font-mono uppercase tracking-wider bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded px-2 py-1 min-h-[36px]"
            data-testid="ops-filter-status"
          >
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filterPm}
            onChange={(e) => setFilterPm(e.target.value)}
            className="text-[11px] font-mono uppercase tracking-wider bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded px-2 py-1 min-h-[36px]"
            data-testid="ops-filter-pm"
          >
            <option value="all">All PMs</option>
            {pms.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>

        {/* Top 5 Problem Projects */}
        {topProblem.length > 0 && (
          <section className="mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)] mb-2">
              Top 5 Problem Projects · Sorted by overdue
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ops-top-problem">
              {topProblem.map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/project/${r.id}`)}
                  className={`text-left px-3 py-3 rounded-md border bg-[var(--fop-surface)] hover:bg-[var(--fop-surface-2)] transition-colors min-h-[88px] ${r.redFlag ? "border-red-500/50" : "border-[var(--fop-border)]"}`}
                  data-testid={`ops-problem-${r.id}`}
                >
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] truncate">
                    {r.project_number || r.id.slice(0,10)}
                  </div>
                  <div className="text-sm font-medium mt-1 truncate">{r.name}</div>
                  <div className="mt-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-wider">
                    <span className={r.overdue > 0 ? "text-red-400" : "text-[var(--fop-text-3)]"}>
                      {r.overdue} OVERDUE
                    </span>
                    <span className="text-[var(--fop-text-2)]">{r.closeoutPct}% CLOSE</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Project table */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)] mb-2">
            All projects
          </div>
          {loading && <div className="text-center py-8 text-[var(--fop-text-3)] text-sm" data-testid="ops-loading">Loading portfolio…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-[var(--fop-text-3)]" data-testid="ops-empty">
              <Building2 size={28} className="mx-auto mb-2" />
              <div className="text-sm text-[var(--fop-text-2)]">No projects match these filters.</div>
              <div className="text-[11px] uppercase tracking-wider font-mono mt-2">
                Reset filters above to see your portfolio.
              </div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider font-mono text-[var(--fop-text-3)] border-b border-[var(--fop-border)]">
                    <th className="text-left px-3 py-2">Project</th>
                    <th className="text-left px-3 py-2">PM</th>
                    <th className="text-right px-3 py-2">Open</th>
                    <th className="text-right px-3 py-2">Overdue</th>
                    <th className="text-right px-3 py-2">Ready</th>
                    <th className="text-right px-3 py-2">Closed</th>
                    <th className="text-right px-3 py-2">Close %</th>
                    <th className="text-right px-3 py-2">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/project/${r.id}`)}
                      className="border-b border-[var(--fop-border)] last:border-b-0 hover:bg-[var(--fop-surface-2)] cursor-pointer"
                      data-testid={`ops-row-${r.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium truncate max-w-[28ch]">{r.name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                          {r.project_number || r.id.slice(0,10)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[12px] text-[var(--fop-text-2)] truncate max-w-[18ch]">{r.pm_name}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{r.open}</td>
                      <td className={`px-3 py-2 text-right font-mono text-[12px] ${r.overdue > 0 ? "text-red-400" : "text-[var(--fop-text-3)]"}`}>{r.overdue}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{r.ready}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px] text-emerald-500">{r.closed}</td>
                      <td className="px-3 py-2 text-right font-mono text-[12px]">{r.closeoutPct}%</td>
                      <td className="px-3 py-2 text-right">
                        {r.redFlag && <AlertTriangle size={14} className="text-red-400 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "neutral" }) {
  const tones = {
    neutral: "text-[var(--fop-text)]",
    ok:       "text-emerald-500",
    warn:     "text-orange-400",
    red:      "text-red-400",
  };
  return (
    <div className="px-3 py-3 rounded-md bg-[var(--fop-surface)] border border-[var(--fop-border)] min-h-[80px]" data-testid={`ops-stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={12} className="text-[var(--fop-text-3)]" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      </div>
      <div className={`text-2xl font-semibold mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}
